
import { debugLogger } from '@/utils/debugLogger';
import { detectPhotoVersion } from '@/utils/photoVersionDetector';
import { CDN_CONFIG, isCdnUrl } from '@/config/cdn';

export type ImageContext = 'avatar' | 'thumbnail' | 'card' | 'display' | 'zoom';

export class ImageUrlService {
  private static instance: ImageUrlService;

  static getInstance(): ImageUrlService {
    if (!ImageUrlService.instance) {
      ImageUrlService.instance = new ImageUrlService();
    }
    return ImageUrlService.instance;
  }

  /**
   * Check if URL is a placeholder that should be ignored from processing
   */
  private isPlaceholderUrl(url: string): boolean {
    if (!url || typeof url !== 'string') return false;
    
    const placeholderPatterns = [
      /\/placeholder\.(svg|jpg|jpeg|png|webp)$/i,
      /placeholder\.(svg|jpg|jpeg|png|webp)$/i,
      /^https:\/\/images\.unsplash\.com/i,
      /^https:\/\/via\.placeholder\.com/i,
      /^data:image/i,
      /\/default-avatar\./i,
      /\/avatar-placeholder\./i
    ];
    
    return placeholderPatterns.some(pattern => pattern.test(url));
  }

  /**
   * Check if URL is a signed URL (Supabase or CDN)
   */
  private isSignedUrl(url: string): boolean {
    if (!url || typeof url !== 'string') return false;
    
    // Must have token parameter
    if (!url.includes('?token=')) return false;
    
    // Supabase signed URLs
    if (url.includes('.supabase.co/storage/v1/object/sign/')) return true;
    
    // CDN signed URLs (storage.interlaced.app/signed-photos/)
    if (url.includes('storage.interlaced.app/signed-photos/')) return true;
    
    return false;
  }

  /**
   * Check if URL is a CDN signed URL (already processed, should not be modified)
   */
  private isCdnSignedUrl(url: string): boolean {
    return isCdnUrl(url) && url.includes('?token=');
  }

  /**
   * Simple validation for profile photo URLs
   */
  isValidProfilePhotoUrl(url: string): boolean {
    if (!url || typeof url !== 'string') return false;
    
    // Placeholders are valid
    if (this.isPlaceholderUrl(url)) return true;
    
    // Basic URL validation for profile photos
    return url.includes('profile_photos') || this.isPlaceholderUrl(url);
  }

  /**
   * SYNCHRONOUS VERSION (for backward compatibility)
   * Get the appropriate image URL for the given context
   * SUPER SIMPLE: just adds the right variant suffix
   */
  getImageUrl(baseUrl: string, context: ImageContext): string {
    if (!baseUrl) return baseUrl;

    // Handle placeholders immediately
    if (this.isPlaceholderUrl(baseUrl)) {
      return baseUrl;
    }

    // Handle signed URLs - return as-is, cannot modify token-based URLs
    if (this.isSignedUrl(baseUrl)) {
      debugLogger.debug('ImageUrlService', 'Signed URL detected, returning as-is', {
        context,
        originalUrl: baseUrl
      });
      return baseUrl;
    }

    // Remove any existing variant from the URL
    const cleanUrl = this.removeExistingVariant(baseUrl);
    
    // Apply the appropriate numeric variant based on context
    let variant: string;
    switch (context) {
      case 'avatar':
        variant = '-0.jpg';  // 80x80
        break;
      case 'thumbnail':
        variant = '-1.jpg';  // 200x200
        break;
      case 'card':
        variant = '-2.jpg';  // 500x500
        break;
      case 'display':
      case 'zoom':
        variant = '-3.jpg';  // 1200x1200 (máximo)
        break;
      default:
        variant = '-1.jpg';
        break;
    }
    
    const finalUrl = this.applyVariant(cleanUrl, variant);
    
    debugLogger.debug('ImageUrlService', 'URL processed', {
      context,
      originalUrl: baseUrl,
      cleanUrl,
      variant,
      finalUrl,
      wasAlreadyVariant: cleanUrl !== baseUrl
    });

    return finalUrl;
  }

  /**
   * ASYNC VERSION: Get optimized image URL for specific context
   * Handles private photos by generating signed URLs with appropriate variants
   * 
   * @param baseUrl - Base image URL
   * @param context - Display context (avatar, thumbnail, card, display, zoom)
   * @returns Promise<Optimized image URL with appropriate variant>
   */
  async getImageUrlAsync(baseUrl: string, context: ImageContext): Promise<string> {
    if (!baseUrl) return baseUrl;

    // 0. CDN signed URLs → already processed, return as-is
    if (this.isCdnSignedUrl(baseUrl)) {
      debugLogger.debug('ImageUrlService', 'CDN signed URL detected, returning as-is', {
        context,
        url: baseUrl.substring(0, 100)
      });
      return baseUrl;
    }

    // 1. Placeholder URLs → return as-is
    if (this.isPlaceholderUrl(baseUrl)) {
      return baseUrl;
    }

    // 2. OLD SIGNED URLs saved in database → extract path and regenerate
    // These are the problematic URLs that expire after 24h
    if (this.isSignedUrl(baseUrl)) {
      debugLogger.debug('ImageUrlService', 'Detected old signed URL from database, extracting path', {
        originalUrl: baseUrl.substring(0, 100),
        context
      });
      
      const filePath = this.extractFilePathFromSignedUrl(baseUrl);
      if (filePath) {
        // Detect photo version to use correct variant format
        const versionInfo = detectPhotoVersion(filePath);
        const isV15 = versionInfo.version === 'v1.5';
        
        // Get variant suffix for context and apply to path (removes existing variant first)
        const variant = this.getVariantSuffix(context, isV15);
        const filePathWithVariant = this.applyVariantToPath(filePath, variant, isV15);
        
        debugLogger.debug('ImageUrlService', 'Regenerating signed URL from old database URL', {
          originalPath: filePath.substring(0, 60),
          context,
          variant,
          newPath: filePathWithVariant.substring(0, 60),
          version: versionInfo.version
        });
        
        const { signedUrlManager } = await import('@/core/services/SignedUrlManager');
        const signedUrl = await signedUrlManager.getSignedUrl(filePathWithVariant, 'profile_photos');
        return signedUrl || this.buildAbsoluteFallbackUrl(filePathWithVariant); // Never return relative path
      }
      return this.buildAbsoluteFallbackUrl(this.extractFilePath(baseUrl));
    }

    // 3. Private profile photos (relative paths) → generate signed URL with variant
    if (this.isPrivateProfilePhoto(baseUrl)) {
      return await this.getPrivatePhotoSignedUrl(baseUrl, context);
    }

    // 4. Private chat media → generate signed URL
    if (this.isPrivateChatMedia(baseUrl)) {
      return await this.getPrivateChatMediaSignedUrl(baseUrl, context);
    }

    // 5. Public URLs → use synchronous path
    return this.getImageUrl(baseUrl, context);
  }

  /**
   * Check if URL is a private profile photo (v1.5 relative path or unsigned URL)
   * Supports both underscore (_) and hyphen (-) variants
   */
  private isPrivateProfilePhoto(url: string): boolean {
    // Detectar path relativo v1.5 com underscore (formato: abc123/def456_0.jpg)
    const isV15RelativePath = /^[a-f0-9]{16}\/[a-f0-9]{16}_[0-3]\.jpg$/i.test(url);
    
    // Detectar path relativo v1 com hyphen (formato: abc123/def456-0.jpg)
    const isV1RelativePath = /^[a-f0-9]{16}\/[a-f0-9]{16}-[0-3]\.jpg$/i.test(url);
    
    // Detectar URL completa sem signed token
    const isUnsignedUrl = url.includes('profile_photos') && 
                         !url.includes('?token=') && 
                         !this.isPlaceholderUrl(url);
    
    return isV15RelativePath || isV1RelativePath || isUnsignedUrl;
  }

  /**
   * Check if URL is a private chat media file
   */
  private isPrivateChatMedia(url: string): boolean {
    // Chat media paths: conversations/uuid/filename.jpg
    const isChatMediaPath = url.includes('chat_media') || 
                           url.includes('conversations/') ||
                           /^conversations\/[a-f0-9-]+\/.+$/i.test(url);
    
    // Must not already be signed
    const isNotSigned = !url.includes('?token=');
    
    return isChatMediaPath && isNotSigned && !this.isPlaceholderUrl(url);
  }

  /**
   * Get signed URL for private chat media
   */
  private async getPrivateChatMediaSignedUrl(baseUrl: string, context: ImageContext): Promise<string> {
    try {
      // Extract file path from URL
      let filePath = baseUrl;
      
      // If it's a full URL, extract the path
      if (baseUrl.includes('chat_media/')) {
        const match = baseUrl.match(/chat_media\/(.+?)(?:\?|$)/);
        filePath = match ? match[1] : baseUrl;
      }
      
      debugLogger.debug('ImageUrlService', 'Generating signed URL for chat media', {
        baseUrl: baseUrl.substring(0, 100),
        filePath,
        context
      });

      // Dynamic import to avoid circular dependency
      const { signedUrlManager } = await import('@/core/services/SignedUrlManager');
      const signedUrl = await signedUrlManager.getSignedUrl(filePath, 'chat_media');
      
      if (signedUrl) {
        debugLogger.debug('ImageUrlService', '✅ Generated signed URL for chat media', {
          baseUrl: baseUrl.substring(0, 60),
          signedUrlPreview: signedUrl.substring(0, 100)
        });
        return signedUrl;
      }
      
      // Never return relative path - build absolute fallback
      return this.buildAbsoluteChatMediaFallbackUrl(filePath);
    } catch (error) {
      debugLogger.error('ImageUrlService', 'Error generating chat media signed URL', {
        baseUrl: baseUrl.substring(0, 100),
        context,
        error
      });
      return this.buildAbsoluteChatMediaFallbackUrl(baseUrl);
    }
  }

  /**
   * Get signed URL for private profile photo with variant
   */
  private async getPrivatePhotoSignedUrl(baseUrl: string, context: ImageContext): Promise<string> {
    try {
      const filePath = this.extractFilePath(baseUrl);
      
      // Detect photo version to use correct variant format
      const versionInfo = detectPhotoVersion(baseUrl);
      const isV15 = versionInfo.version === 'v1.5';
      
      if (isV15) {
        debugLogger.debug('ImageUrlService', '🔐 Detected v1.5 private photo', {
          baseUrl: baseUrl.substring(0, 60),
          filePath: filePath.substring(0, 60)
        });
      }
      
      const variant = this.getVariantSuffix(context, isV15);
      const filePathWithVariant = this.applyVariantToPath(filePath, variant, isV15);
      
      debugLogger.debug('ImageUrlService', 'Generating signed URL for private photo', {
        baseUrl: baseUrl.substring(0, 100),
        filePath,
        variant,
        filePathWithVariant,
        version: versionInfo.version
      });

      // Dynamic import to avoid circular dependency
      const { signedUrlManager } = await import('@/core/services/SignedUrlManager');
      const signedUrl = await signedUrlManager.getSignedUrl(filePathWithVariant, 'profile_photos');
      
      if (signedUrl) {
        debugLogger.debug('ImageUrlService', '✅ Generated signed URL', {
          baseUrl: baseUrl.substring(0, 60),
          signedUrlPreview: signedUrl.substring(0, 100)
        });
        return signedUrl;
      }
      
      // Never return relative path - build absolute fallback
      debugLogger.warn('ImageUrlService', 'Signed URL generation failed, using absolute fallback', {
        baseUrl: baseUrl.substring(0, 60),
        filePathWithVariant
      });
      return this.buildAbsoluteFallbackUrl(filePathWithVariant);
    } catch (error) {
      debugLogger.error('ImageUrlService', 'Error generating signed URL', {
        baseUrl: baseUrl.substring(0, 100),
        context,
        error
      });
      return this.buildAbsoluteFallbackUrl(this.extractFilePath(baseUrl));
    }
  }

  /**
   * Build absolute fallback URL for profile photos
   * Ensures we never return a relative path that would resolve against current page
   */
  private buildAbsoluteFallbackUrl(filePath: string): string {
    return `${CDN_CONFIG.baseUrl}${CDN_CONFIG.paths.signedPhotos}${filePath}`;
  }

  /**
   * Build absolute fallback URL for chat media
   */
  private buildAbsoluteChatMediaFallbackUrl(filePath: string): string {
    // Chat media uses a different path structure
    return `${CDN_CONFIG.baseUrl}/chat-media/${filePath}`;
  }

  /**
   * Extract file path from full URL or return relative path as-is
   * Supports both v1.5 underscore and v1 hyphen formats
   */
  private extractFilePath(url: string): string {
    // Se já é path relativo v1.5 com underscore, retornar direto
    if (/^[a-f0-9]{16}\/[a-f0-9]{16}_[0-3]\.jpg$/i.test(url)) {
      return url;
    }
    
    // Se já é path relativo v1 com hyphen, retornar direto
    if (/^[a-f0-9]{16}\/[a-f0-9]{16}-[0-3]\.jpg$/i.test(url)) {
      return url;
    }
    
    // Extrair de URL completa
    const match = url.match(/profile_photos\/(.+?)(?:\?|$)/);
    return match ? match[1] : url;
  }

  /**
   * Extract file path from old signed URL
   */
  private extractFilePathFromSignedUrl(signedUrl: string): string | null {
    // Extract path from signed URL: 
    // .../sign/profile_photos/abc/def-0.jpg?token=... → abc/def-0.jpg
    const match = signedUrl.match(/profile_photos\/(.+?)\?token=/);
    return match ? match[1] : null;
  }

  /**
   * Apply variant suffix to file path
   */
  private applyVariantToPath(filePath: string, variant: string, isV15: boolean = false): string {
    // 'abc123/def456.jpg' + '-0' → 'abc123/def456-0.jpg' (v1)
    // 'abc123/def456.jpg' + '_0' → 'abc123/def456_0.jpg' (v1.5)
    const variantPattern = isV15 ? /_[0-3]\.jpg$/i : /-[0-3]\.jpg$/i;
    const cleanPath = filePath.replace(variantPattern, '.jpg');
    return cleanPath.replace(/\.jpg$/i, `${variant}.jpg`);
  }

  /**
   * Get variant suffix for context
   */
  private getVariantSuffix(context: ImageContext, isV15: boolean = false): string {
    const prefix = isV15 ? '_' : '-';
    
    switch (context) {
      case 'avatar':
        return `${prefix}0`; // 80x80
      case 'thumbnail':
        return `${prefix}1`; // 200x200
      case 'card':
        return `${prefix}2`; // 500x500
      case 'display':
      case 'zoom':
        return `${prefix}3`; // 1200x1200
      default:
        return `${prefix}1`;
    }
  }

  /**
   * Remove existing variant from URL (supports both numeric and old semantic variants)
   */
  private removeExistingVariant(url: string): string {
    const urlParts = url.split('/');
    const fileName = urlParts[urlParts.length - 1];
    
    // Remove known variants from filename:
    // - v1.5: underscore variants (_0, _1, _2, _3)
    // - v1: numeric variants (-0, -1, -2, -3) OR old semantic names
    const cleanFileName = fileName.replace(/[_-](?:\d|ultra-thumb|thumb|medium|large|small|original|medium-display|full-quality|micro-avatar|small-thumb|medium-card|large-display)\.jpg$/i, '.jpg');
    
    // Reconstruct URL
    urlParts[urlParts.length - 1] = cleanFileName;
    return urlParts.join('/');
  }

  /**
   * Apply variant to clean URL
   */
  private applyVariant(cleanUrl: string, variant: string): string {
    // Replace extension with variant
    const lastDotIndex = cleanUrl.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return `${cleanUrl}${variant}`;
    }
    
    const baseName = cleanUrl.substring(0, lastDotIndex);
    return `${baseName}${variant}`;
  }

  /**
   * Get old variant names for backward compatibility (includes both semantic and numeric)
   */
  private getOldVariantMapping(context: ImageContext): string[] {
    const oldVariants: Record<ImageContext, string[]> = {
      'avatar': ['-micro-avatar', '-small-thumb', '-0', '-1'],
      'thumbnail': ['-small-thumb', '-micro-avatar', '-1', '-0'],
      'card': ['-medium-card', '-small-thumb', '-2', '-1'],
      'display': ['-large-display', '-medium-card', '-3', '-2'],
      'zoom': ['-large-display', '-medium-card', '-3', '-2']
    };
    return oldVariants[context];
  }

  /**
   * Generate intelligent fallbacks with backward compatibility
   */
  generateFallbackUrls(originalUrl: string, context: ImageContext): string[] {
    if (this.isPlaceholderUrl(originalUrl)) {
      return [];
    }

    // Signed URLs cannot have fallbacks - would need to regenerate signed URLs for each variant
    if (this.isSignedUrl(originalUrl)) {
      debugLogger.debug('ImageUrlService', 'Cannot generate fallbacks for signed URLs', {
        originalUrl,
        context
      });
      return [];
    }
    
    const cleanUrl = this.removeExistingVariant(originalUrl);
    const fallbacks: string[] = [];
    
    // 1. Try corresponding old variants first (for backward compatibility)
    const oldVariants = this.getOldVariantMapping(context);
    oldVariants.forEach(oldVariant => {
      const oldVariantUrl = this.applyVariant(cleanUrl, oldVariant);
      if (oldVariantUrl !== originalUrl && !fallbacks.includes(oldVariantUrl)) {
        fallbacks.push(oldVariantUrl);
      }
    });
    
    // 2. Try clean URL (original without variant)
    if (cleanUrl !== originalUrl && !fallbacks.includes(cleanUrl)) {
      fallbacks.push(cleanUrl);
    }
    
    // 3. Try other common old variants as last resort
    const commonOldVariants = ['-ultra-thumb.jpg', '-medium-display.jpg', '-full-quality.jpg'];
    commonOldVariants.forEach(variant => {
      if (!oldVariants.includes(variant)) {
        const fallbackUrl = this.applyVariant(cleanUrl, variant);
        if (!fallbacks.includes(fallbackUrl)) {
          fallbacks.push(fallbackUrl);
        }
      }
    });
    
    debugLogger.debug('ImageUrlService', 'Generated fallbacks for backward compatibility', {
      originalUrl,
      context,
      oldVariants,
      fallbackCount: fallbacks.length
    });
    
    return fallbacks;
  }

  /**
   * Handle image load error with context-aware fallbacks
   */
  handleImageLoadError(originalUrl: string, context: ImageContext): string[] {
    if (this.isPlaceholderUrl(originalUrl)) {
      return [];
    }
    
    debugLogger.warn('ImageUrlService', 'Image load failed, generating context-aware fallbacks', { originalUrl, context });
    return this.generateFallbackUrls(originalUrl, context);
  }
}

export const imageUrlService = ImageUrlService.getInstance();
